import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@shared";
import { NavigationMenu, Logo } from "@printtat/ui";
import { cn } from "@/lib/utils";
import { UserMenu } from "./UserMenu";
import { LayoutDashboard, ImageIcon, ShoppingBag, Store, Package, Shield } from "lucide-react";
import { UserRole } from "@printtat/shared";

interface NavigationProps {
  className?: string;
}

export const Navigation = ({ className }: NavigationProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const isActive = (path: string) => location.pathname === path;

  return (
    <div className="h-screen sticky top-0 p-3 w-60 flex-shrink-0">
      <div className="border rounded-2xl bg-white h-full flex flex-col p-2.5">
        {/* Logo/Brand */}
        <div className="px-2 py-1.5 mb-6">
          <button
            onClick={() => navigate("/dashboard")}
            className="cursor-pointer"
          >
            <Logo label="Beta" />
          </button>
        </div>

        {/* Main Navigation */}
        <NavigationMenu.Root
          orientation="vertical"
          className={cn("flex flex-col", className)}
        >
          <NavigationMenu.List className="flex flex-col space-y-1">
            <NavigationMenu.Item>
              <NavigationMenu.Link
                asChild
                active={isActive("/dashboard")}
                className={cn(
                  "w-full text-left px-2.5 py-1.5 text-[13px] font-medium rounded-lg transition-colors",
                  "hover:text-gray-900 hover:bg-gray-100",
                  isActive("/dashboard")
                    ? "text-gray-900 bg-gray-100"
                    : "text-gray-500"
                )}
              >
                <button onClick={() => navigate("/dashboard")} className="flex items-center gap-2 w-full">
                  <LayoutDashboard className="w-3.5 h-3.5" />
                  Dashboard
                </button>
              </NavigationMenu.Link>
            </NavigationMenu.Item>
            <NavigationMenu.Item>
              <NavigationMenu.Link
                asChild
                active={location.pathname.startsWith("/catalog")}
                className={cn(
                  "w-full text-left px-2.5 py-1.5 text-[13px] font-medium rounded-lg transition-colors",
                  "hover:text-gray-900 hover:bg-gray-100",
                  location.pathname.startsWith("/catalog")
                    ? "text-gray-900 bg-gray-100"
                    : "text-gray-500"
                )}
              >
                <button onClick={() => navigate("/catalog")} className="flex items-center gap-2 w-full">
                  <Store className="w-3.5 h-3.5" />
                  Explore
                </button>
              </NavigationMenu.Link>
            </NavigationMenu.Item>
            <NavigationMenu.Item>
              <NavigationMenu.Link
                asChild
                active={isActive("/products")}
                className={cn(
                  "w-full text-left px-2.5 py-1.5 text-[13px] font-medium rounded-lg transition-colors",
                  "hover:text-gray-900 hover:bg-gray-100",
                  isActive("/products")
                    ? "text-gray-900 bg-gray-100"
                    : "text-gray-500"
                )}
              >
                <button onClick={() => navigate("/products")} className="flex items-center gap-2 w-full">
                  <ShoppingBag className="w-3.5 h-3.5" />
                  Products
                </button>
              </NavigationMenu.Link>
            </NavigationMenu.Item>
            <NavigationMenu.Item>
              <NavigationMenu.Link
                asChild
                active={isActive("/orders")}
                className={cn(
                  "w-full text-left px-2.5 py-1.5 text-[13px] font-medium rounded-lg transition-colors",
                  "hover:text-gray-900 hover:bg-gray-100",
                  isActive("/orders")
                    ? "text-gray-900 bg-gray-100"
                    : "text-gray-500"
                )}
              >
                <button onClick={() => navigate("/orders")} className="flex items-center gap-2 w-full">
                  <Package className="w-3.5 h-3.5" />
                  Orders
                </button>
              </NavigationMenu.Link>
            </NavigationMenu.Item>
            <NavigationMenu.Item>
              <NavigationMenu.Link
                asChild
                active={isActive("/assets")}
                className={cn(
                  "w-full text-left px-2.5 py-1.5 text-[13px] font-medium rounded-lg transition-colors",
                  "hover:text-gray-900 hover:bg-gray-100",
                  isActive("/assets")
                    ? "text-gray-900 bg-gray-100"
                    : "text-gray-500"
                )}
              >
                <button onClick={() => navigate("/assets")} className="flex items-center gap-2 w-full">
                  <ImageIcon className="w-3.5 h-3.5" />
                  Library
                </button>
              </NavigationMenu.Link>
            </NavigationMenu.Item>
          </NavigationMenu.List>
        </NavigationMenu.Root>

        {/* Admin link + User Menu - pinned to bottom */}
        {user && (
          <div className="mt-auto space-y-1">
            {user.role === UserRole.ADMIN && (
              <a
                href={import.meta.env.VITE_ADMIN_URL || 'http://localhost:5178'}
                className="flex items-center gap-2 w-full px-2.5 py-1.5 text-[13px] font-medium text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <Shield className="w-3.5 h-3.5" />
                Admin
              </a>
            )}
            <UserMenu />
          </div>
        )}
      </div>
    </div>
  );
};
